/**
 * TanStack Query hooks — Missing Information (Chunk 9.L.12 — broker UI).
 *
 * Backend :
 *   - apps/api/src/routes/missing-information.ts
 *       GET    /v1/missing-information?project_id|submission_id|...&status     (9.F lecture, scope submissions:read)
 *       GET    /v1/missing-information/:id                                     (9.F lecture, scope submissions:read)
 *       PATCH  /v1/missing-information/:id/fulfill                             (9.I.1, scope submissions:write, gating actif)
 *       PATCH  /v1/missing-information/:id/waive                               (9.I.2, scope submissions:write, gating actif)
 *       PATCH  /v1/missing-information/:id/reopen                              (9.I.2, scope submissions:write, gating actif)
 *       PATCH  /v1/missing-information/:id/supersede                           (9.I.2, scope submissions:write, gating actif)
 *
 * Wire shapes confirmées 9.L.12 diagnostic :
 *   - LIST   : `data = { items: MissingInformationRow[], next_cursor: string|null }`
 *   - DETAIL : `data = MissingInformationRow`
 *   - PATCH transitions : `data = MissingInformationRow` (row complète post-update,
 *     re-SELECT côté backend — parité 9.I.1+9.I.2).
 *
 * Codes erreur attendus côté UI (bilingues) :
 *   MISSING_INFORMATION_NOT_FOUND (404)
 *   MISSING_INFORMATION_INVALID_TRANSITION (409)
 *   GATING_HUMAN_REQUIRED (403, broker JWT bypass naturel)
 *   VALIDATION_ERROR (400)
 *
 * Convention queryKey :
 *   ['missing-information', 'by-project', { projectId, status, audience, cursor, limit }]
 *   ['missing-information', 'by-submission', { submissionId, status, audience, cursor, limit }]
 *   ['missing-information', 'detail', id]
 *
 * Invalidation post-mutation :
 *   - by-project + by-submission (ratisser large — Phase 1 cardinalité faible)
 *   - detail si id connu
 */

import type {
  FulfillMissingInformationInput,
  MissingInfoAudience,
  MissingInfoStatus,
  MissingInformationRow,
  ReopenMissingInformationInput,
  SupersedeMissingInformationInput,
  WaiveMissingInformationInput,
} from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

// -----------------------------------------------------------------------------
// Types — re-export ergonomiques
// -----------------------------------------------------------------------------

export type MissingInformationListResult = {
  items: MissingInformationRow[];
  next_cursor: string | null;
};

type CommonListParams = {
  status?: MissingInfoStatus;
  audience?: MissingInfoAudience;
  cursor?: string | null;
  limit?: number;
  enabled?: boolean;
};

export type MissingInformationByProjectParams = CommonListParams & {
  projectId: string | undefined;
};

export type MissingInformationBySubmissionParams = CommonListParams & {
  submissionId: string | undefined;
};

// -----------------------------------------------------------------------------
// GET /v1/missing-information — listes filtrées (parité backend 9.F)
// -----------------------------------------------------------------------------

function buildListPath(
  scopeKey: "project_id" | "submission_id",
  scopeValue: string,
  params: CommonListParams,
): string {
  const sp = new URLSearchParams();
  sp.set(scopeKey, scopeValue);
  if (params.status) sp.set("status", params.status);
  if (params.audience) sp.set("audience", params.audience);
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  return `/v1/missing-information?${sp.toString()}`;
}

export function useMissingInformationByProject(
  params: MissingInformationByProjectParams,
) {
  return useQuery({
    queryKey: [
      "missing-information",
      "by-project",
      {
        projectId: params.projectId,
        status: params.status,
        audience: params.audience,
        cursor: params.cursor,
        limit: params.limit,
      },
    ],
    queryFn: () =>
      api.get<MissingInformationListResult>(
        buildListPath("project_id", params.projectId!, params),
      ),
    enabled: (params.enabled ?? true) && !!params.projectId,
  });
}

export function useMissingInformationBySubmission(
  params: MissingInformationBySubmissionParams,
) {
  return useQuery({
    queryKey: [
      "missing-information",
      "by-submission",
      {
        submissionId: params.submissionId,
        status: params.status,
        audience: params.audience,
        cursor: params.cursor,
        limit: params.limit,
      },
    ],
    queryFn: () =>
      api.get<MissingInformationListResult>(
        buildListPath("submission_id", params.submissionId!, params),
      ),
    enabled: (params.enabled ?? true) && !!params.submissionId,
  });
}

// -----------------------------------------------------------------------------
// Mutations — transitions 9.I.1+9.I.2 broker-facing
// -----------------------------------------------------------------------------

function invalidateLists(qc: ReturnType<typeof useQueryClient>, id: string) {
  void qc.invalidateQueries({ queryKey: ["missing-information", "by-project"] });
  void qc.invalidateQueries({
    queryKey: ["missing-information", "by-submission"],
  });
  void qc.invalidateQueries({ queryKey: ["missing-information", "detail", id] });
}

/** Vars PATCH /v1/missing-information/:id/fulfill — gating Couche 2 actif. */
export type FulfillMissingInformationMutationVars = {
  missingInformationId: string;
  input: FulfillMissingInformationInput;
};

/**
 * Fulfill une MI — transition `open|in_progress → fulfilled` + persiste
 * `fulfilled_value` jsonb. Default gating `agent_allowed=false` (broker JWT
 * bypass naturel).
 */
export function useFulfillMissingInformation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      missingInformationId,
      input,
    }: FulfillMissingInformationMutationVars) =>
      api.patch<MissingInformationRow>(
        `/v1/missing-information/${missingInformationId}/fulfill`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (_data, { missingInformationId }) => {
      invalidateLists(qc, missingInformationId);
    },
  });
}

/** Vars PATCH /v1/missing-information/:id/waive — `waiver_reason` 1..500. */
export type WaiveMissingInformationMutationVars = {
  missingInformationId: string;
  input: WaiveMissingInformationInput;
};

/**
 * Waive une MI — transition `open|in_progress → waived` + persiste
 * `waiver_reason`. Default gating `agent_allowed=false`.
 */
export function useWaiveMissingInformation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      missingInformationId,
      input,
    }: WaiveMissingInformationMutationVars) =>
      api.patch<MissingInformationRow>(
        `/v1/missing-information/${missingInformationId}/waive`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (_data, { missingInformationId }) => {
      invalidateLists(qc, missingInformationId);
    },
  });
}

/** Vars PATCH /v1/missing-information/:id/reopen — body vide Phase 1. */
export type ReopenMissingInformationMutationVars = {
  missingInformationId: string;
  input?: ReopenMissingInformationInput;
};

/**
 * Reopen une MI — transition `fulfilled|waived → open` (reset destructif
 * `fulfilled_*` + `waiver_reason`). Default gating `agent_allowed=false`.
 */
export function useReopenMissingInformation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      missingInformationId,
      input,
    }: ReopenMissingInformationMutationVars) =>
      api.patch<MissingInformationRow>(
        `/v1/missing-information/${missingInformationId}/reopen`,
        (input ?? {}) as unknown as Record<string, unknown>,
      ),
    onSuccess: (_data, { missingInformationId }) => {
      invalidateLists(qc, missingInformationId);
    },
  });
}

/** Vars PATCH /v1/missing-information/:id/supersede — body vide Phase 1. */
export type SupersedeMissingInformationMutationVars = {
  missingInformationId: string;
  input?: SupersedeMissingInformationInput;
};

/**
 * Supersede une MI — transition `open|in_progress|fulfilled|waived → superseded`
 * (sémantique pure, pas de `superseded_by_id` Phase 1 — dette P3 9.I.3).
 * Default gating `agent_allowed=false`.
 */
export function useSupersedeMissingInformation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      missingInformationId,
      input,
    }: SupersedeMissingInformationMutationVars) =>
      api.patch<MissingInformationRow>(
        `/v1/missing-information/${missingInformationId}/supersede`,
        (input ?? {}) as unknown as Record<string, unknown>,
      ),
    onSuccess: (_data, { missingInformationId }) => {
      invalidateLists(qc, missingInformationId);
    },
  });
}
